import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Download, Building2, Calendar, DollarSign, RefreshCw, Shield, FileText } from "lucide-react";
import { downloadTenantAbstract } from "../api/client";
import useEscapeKey from "../hooks/useEscapeKey";
import { fmtCurrency, fmtPSF, fmtSF, fmtDate, fmtPercent } from "../utils/format";
import { prettyFieldLabel } from "../utils/labels";

interface RentStep {
  year: number;
  rent_annual_psf?: number;
  rent_psf?: number;
}

interface RenewalOption {
  term_years?: number;
  term_months?: number;
  rent_description?: string;
  notice_months?: number;
}

interface CamStructureData {
  cam_type?: string;
  base_year?: number | null;
  expense_cap_pct?: number | null;
  management_fee_pct?: number | null;
  admin_fee_pct?: number | null;
  tax_reimbursement?: boolean;
  insurance_reimbursement?: boolean;
}

interface TenantData {
  tenant_name: string;
  suite_id?: string | null;
  leased_sf: number;
  pro_rata_share?: number | null;
  space_status?: string;
  lease_type?: string;
  lease_start_date?: string;
  lease_expiration_date?: string;
  base_rent_annual_psf: number;
  rent_escalation_type?: string;
  rent_escalation_value?: number;
  rent_steps?: RentStep[];
  free_rent_months?: number;
  renewal_options?: RenewalOption[];
  cam_structure?: CamStructureData;
  credit_rating?: string | null;
  credit_notes?: string | null;
  security_deposit?: number;
}

interface Props {
  tenant: TenantData;
  propertyName: string;
  dealId: string;
  tenantIdx: number;
  onClose: () => void;
}

function SectionHeader({ icon: Icon, title }: { icon: React.ComponentType<{ className?: string }>; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-1 border-b border-grove-200">
      <Icon className="w-4 h-4 text-forest-700" />
      <h4 className="font-display text-sm font-semibold text-forest-700">{title}</h4>
    </div>
  );
}

function KVRow({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex py-1.5 text-sm border-b border-grove-100 last:border-b-0">
      <span className="w-52 flex-shrink-0 font-medium text-bark-500">{label}</span>
      <span className="text-bark-700">{value}</span>
    </div>
  );
}

function calcTermLength(start?: string, end?: string): string {
  if (!start || !end) return "\u2014";
  try {
    const s = new Date(start);
    const e = new Date(end);
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    const years = Math.floor(months / 12);
    const rem = months % 12;
    if (rem === 0) return `${years} years (${months} mo)`;
    return `${years} yrs ${rem} mo (${months} mo)`;
  } catch {
    return "\u2014";
  }
}

function fmtEscalation(type?: string, value?: number): string {
  const raw = type || "flat";
  if (raw === "flat") return "Flat (no escalation)";
  if (raw === "fixed_percent" && value != null) return `${fmtPercent(value, 1)} annually`;
  if (raw === "fixed_amount" && value != null) return `${fmtPSF(value)} annually`;
  if (raw.toLowerCase() === "cpi" && value != null) return `CPI (${fmtPercent(value, 1)} factor)`;
  return prettyFieldLabel(raw);
}

export default function LeaseAbstractModal({ tenant, propertyName, dealId, tenantIdx, onClose }: Props) {
  useEscapeKey(onClose);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await downloadTenantAbstract(dealId, tenantIdx);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tenant.tenant_name} - Lease Abstract.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [dealId, tenantIdx, tenant.tenant_name]);

  const cam = tenant.cam_structure || {};
  const rentSteps = tenant.rent_steps || [];
  const renewals = tenant.renewal_options || [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-grove-200 my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grove-200 bg-grove-50 rounded-t-xl">
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-semibold text-bark-800 truncate">
              {tenant.tenant_name}
            </h3>
            <p className="text-sm text-bark-400 mt-0.5">
              {tenant.suite_id ? `Suite ${tenant.suite_id}` : ""} {propertyName ? `\u2014 ${propertyName}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-forest-700 rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-all"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Downloading..." : "Download PDF"}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-bark-400 hover:text-bark-600 hover:bg-grove-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Section 1: Premises */}
          <section>
            <SectionHeader icon={Building2} title="Premises" />
            <KVRow label="Tenant" value={tenant.tenant_name} />
            <KVRow label="Suite / Unit" value={tenant.suite_id || "\u2014"} />
            <KVRow label="Leased Area" value={fmtSF(tenant.leased_sf)} />
            <KVRow
              label="Pro-Rata Share"
              value={tenant.pro_rata_share != null ? fmtPercent(tenant.pro_rata_share) : "Calculated"}
            />
            <KVRow
              label="Status"
              value={
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  (tenant.space_status || "leased") === "leased"
                    ? "bg-forest-100 text-forest-700"
                    : "bg-honey-100 text-honey-800"
                }`}>
                  {prettyFieldLabel(tenant.space_status || "leased")}
                </span>
              }
            />
          </section>

          {/* Section 2: Lease Term */}
          <section>
            <SectionHeader icon={Calendar} title="Lease Term" />
            <KVRow label="Commencement" value={fmtDate(tenant.lease_start_date) || "\u2014"} />
            <KVRow label="Expiration" value={fmtDate(tenant.lease_expiration_date) || "\u2014"} />
            <KVRow label="Term Length" value={calcTermLength(tenant.lease_start_date, tenant.lease_expiration_date)} />
            <KVRow label="Free Rent" value={tenant.free_rent_months ? `${tenant.free_rent_months} months` : "None"} />
          </section>

          {/* Section 3: Rent Schedule */}
          <section>
            <SectionHeader icon={DollarSign} title="Rent Schedule" />
            <KVRow label="Base Rent PSF" value={fmtPSF(tenant.base_rent_annual_psf)} />
            <KVRow label="Annual Base Rent" value={fmtCurrency(tenant.base_rent_annual_psf * tenant.leased_sf)} />
            <KVRow label="Escalation" value={fmtEscalation(tenant.rent_escalation_type, tenant.rent_escalation_value)} />

            {rentSteps.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-bark-500 mb-1.5">Rent Step Schedule</p>
                <div className="border border-grove-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-forest-700 text-white text-xs">
                        <th className="px-3 py-1.5 text-right font-medium">Year</th>
                        <th className="px-3 py-1.5 text-right font-medium">Rent PSF</th>
                        <th className="px-3 py-1.5 text-right font-medium">Annual Rent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rentSteps.map((step, i) => {
                        const psf = step.rent_annual_psf ?? step.rent_psf ?? 0;
                        return (
                          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-grove-50"}>
                            <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{step.year}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtPSF(psf)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtCurrency(psf * tenant.leased_sf)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Section 4: Renewal Options */}
          <section>
            <SectionHeader icon={RefreshCw} title="Renewal Options" />
            {renewals.length === 0 ? (
              <p className="text-sm text-bark-400 italic">None specified</p>
            ) : (
              <div className="space-y-2">
                {renewals.map((opt, i) => {
                  const term = opt.term_years
                    ? `${opt.term_years} years`
                    : opt.term_months
                    ? `${opt.term_months} months`
                    : "\u2014";
                  return (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-forest-600 font-semibold whitespace-nowrap">Option {i + 1}:</span>
                      <span className="text-bark-700">
                        {term}
                        {opt.notice_months ? ` (${opt.notice_months} mo notice)` : ""}
                        {opt.rent_description ? ` \u2014 ${opt.rent_description}` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Section 5: Reimbursement Structure */}
          <section>
            <SectionHeader icon={FileText} title="Reimbursement Structure" />
            <KVRow label="Lease Type" value={prettyFieldLabel(tenant.lease_type || "\u2014").toUpperCase()} />
            <KVRow label="CAM Type" value={prettyFieldLabel(cam.cam_type || "\u2014").toUpperCase()} />
            {cam.base_year != null && <KVRow label="Base Year" value={String(cam.base_year)} />}
            {cam.expense_cap_pct != null && <KVRow label="Expense Cap" value={fmtPercent(cam.expense_cap_pct)} />}
            {(cam.management_fee_pct ?? cam.admin_fee_pct) ? (
              <KVRow label="Admin / Mgmt Fee" value={fmtPercent(cam.management_fee_pct ?? cam.admin_fee_pct ?? 0)} />
            ) : null}
            <KVRow label="Tax Reimbursement" value={cam.tax_reimbursement !== false ? "Yes" : "No"} />
            <KVRow label="Insurance Reimbursement" value={cam.insurance_reimbursement !== false ? "Yes" : "No"} />
          </section>

          {/* Section 6: Additional Terms */}
          <section>
            <SectionHeader icon={Shield} title="Additional Terms" />
            <KVRow
              label="Security Deposit"
              value={tenant.security_deposit ? fmtCurrency(tenant.security_deposit) : "None"}
            />
            {tenant.credit_rating && <KVRow label="Credit Rating" value={tenant.credit_rating} />}
            {tenant.credit_notes && <KVRow label="Credit Notes" value={tenant.credit_notes} />}
            {!tenant.credit_rating && !tenant.credit_notes && (
              <KVRow label="Credit" value="No credit information available" />
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
}
